import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { NextActionCard } from "../components/shared/NextActionCard";
import { EmptyState } from "../components/shared/EmptyState";
import { Button } from "../components/ui/button";
import { VoiceButton } from "../components/shared/VoiceButton";
import { toast } from "sonner";
import { capture, EVENTS } from "../analytics/posthog";
import { Compass, ArrowRight, Send, Sparkles, Check, X, Loader2 } from "lucide-react";

function resumeLine(ctx) {
  const o = ctx.current_objective, s = ctx.current_system;
  if (o?.objective) {
    let t = `Seguimos con tu objetivo: ${o.objective}.`;
    if (s?.title) t += ` El sistema «${s.title}» va ${s.milestones_done}/${s.milestones_total} hitos.`;
    return t;
  }
  if (ctx.top_open_dependency?.title) return `Tu mayor dependencia abierta ahora es: ${ctx.top_open_dependency.title}.`;
  return "Vamos a empezar a reducir la dependencia de tu empresa respecto a ti.";
}

export default function ArchitectHome() {
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [cta, setCta] = useState(null);
  const endRef = useRef(null);
  const architectUsedRef = useRef(false);

  const loadCtx = async () => {
    try { const { data } = await api.get("/architect/home"); setCtx(data); } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => {
    (async () => {
      await loadCtx();
      try { const { data } = await api.get("/architect/session"); setSessionId(data.id); setMessages(data.messages || []); } catch {}
      setLoading(false);
    })();
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput(""); setProposal(null); setCta(null);
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/architect/chat", { message: msg, session_id: sessionId });
      if (!architectUsedRef.current) { architectUsedRef.current = true; capture(EVENTS.architectUsed); }
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.proposal) setProposal(data.proposal);
      if (data.cta) setCta(data.cta);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSending(false); }
  };

  const confirmProposal = async () => {
    try {
      const { data } = await api.post("/architect/confirm", { proposal });
      setProposal(null);
      setMessages((m) => [...m, { role: "assistant", content: data.message || "Hecho." }]);
      if (data.kind === "evidence") window.dispatchEvent(new Event("credits:refresh"));
      await loadCtx();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <div className="mono-label text-slate-500">CARGANDO…</div>;

  if (!ctx?.has_audit) {
    return (
      <EmptyState icon={Compass} title="Empecemos por conocer tu empresa"
        description="Haz el diagnóstico conversacional para que Architect entienda de qué depende hoy tu negocio y te guíe paso a paso."
        testId="architect-empty-audit"
        action={<Button data-testid="architect-start-audit" onClick={() => navigate("/audit")} className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold">Comenzar diagnóstico <ArrowRight className="ml-2 h-4 w-4" /></Button>} />
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Architect context + resume */}
      <div className="card-arch p-5 sm:p-6" data-testid="architect-context">
        <div className="halo" />
        <div className="relative">
          <div className="flex items-center gap-2 text-amber-400"><Sparkles className="h-4 w-4" /><span className="mono-label">Architect</span></div>
          <p className="mt-3 text-white leading-relaxed" data-testid="architect-resume">{resumeLine(ctx)}</p>
          {ctx.independence && (
            <p className="mt-2 text-xs text-slate-500">Independence Score {ctx.independence.score} · {ctx.independence.category}</p>
          )}
        </div>
      </div>

      {/* Single next best action */}
      {ctx.next_action && <NextActionCard action={ctx.next_action} />}

      {/* Conversation */}
      <div className="space-y-3" data-testid="architect-thread">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-amber-500 text-black" : "bg-white/[0.05] text-slate-200 border border-white/10"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && <div className="flex justify-start"><div className="rounded-2xl px-4 py-2.5 bg-white/[0.05] border border-white/10"><Loader2 className="h-4 w-4 animate-spin text-amber-400" /></div></div>}

        {proposal && !sending && (
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="architect-proposal">
            <p className="text-sm text-slate-200">{proposal.human}</p>
            <div className="mt-3 flex gap-2">
              <Button data-testid="architect-proposal-confirm" onClick={confirmProposal} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-full font-semibold"><Check className="mr-1.5 h-4 w-4" /> Confirmar</Button>
              <Button data-testid="architect-proposal-dismiss" onClick={() => setProposal(null)} variant="outline" className="bg-white/[0.03] border-white/10 text-white rounded-full"><X className="mr-1.5 h-4 w-4" /> Ahora no</Button>
            </div>
          </div>
        )}
        {cta && !sending && (
          <div className="flex justify-start">
            <Button data-testid="architect-cta" onClick={() => navigate(cta.path)} className="bg-white/[0.06] text-white hover:bg-white/[0.1] rounded-full font-semibold">{cta.label} <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="sticky bottom-0 z-20 pt-2 pb-1 pb-safe bg-obsidian/90 backdrop-blur-xl">
        <div className="flex items-end gap-2">
          <input data-testid="architect-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Escribe qué ha pasado o qué quieres conseguir…"
            className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/40" />
          <VoiceButton testId="architect-voice-button" onTranscript={(t) => setInput((p) => (p ? p + " " : "") + t)} />
          <Button data-testid="architect-send" onClick={() => send()} disabled={sending || !input.trim()}
            className="h-11 shrink-0 rounded-xl bg-amber-500 text-black hover:bg-amber-400 font-semibold px-4"><Send className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
